import React, { useContext, useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Collapse,
  Form,
  FormGroup,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import {
  Drawer,
  Switch,
  Tooltip,
  Table as AntdTable,
  Input as AntdInput,
  Form as AntdForm,
  Select,
} from "antd"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  generatePassword,
  getLottieLoadingOptions,
  showToast,
  checkboxFormatter,
  stringSorter,
  booleanSorter,
  defaultPageSizeOptions,
} from "utils/general"
import { isEqual } from "lodash"
import {
  getApplicationUser,
  getAppUsersLinks,
  linkClientsToAppUser,
  sendAppUserCredentials,
  unlinkClientsFromAppUser,
  updateApplicationUser,
} from "helpers/backend_helper"
import Lottie from "react-lottie"
import classnames from "classnames"
import SelectGroupFilters from "components/DropdownList/SelectGroupFilters"
import TagsTree from "components/DropdownList/TagsTree"
import GlobalState from "contexts/GlobalState"
import withRouter from "components/Common/withRouter"
import { tableDefaultHeight } from "constants/layout"

const { Search, TextArea } = AntdInput

const AppUserDrawer = props => {
  const [state] = useContext(GlobalState)
  const [userRole, setUserRole] = useState(null)
  const [activeTab, setActiveTab] = useState("1")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [update, setUpdate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [userData, setUserData] = useState(null)
  const [sms, setSms] = useState(
    "Lataa Suvanto Care -sovellus osoitteesta Suvantocare.fi/lataa tai puhelimesi sovelluskaupasta. Löydät lisää ohjeita osoitteesta https://suvanto.help. \n\nMobiilikäyttäjätunnus on muotoa etu.sukunimi ja salasana"
  )
  const [selectedClients, setSelectedClients] = useState([])
  const [clients, setClients] = useState([])
  const [filteredClients, setFilteredClients] = useState([])
  const [linksGroup, setLinksGroup] = useState([])
  const [searchText, setSearchText] = useState("")
  const [selectedTags, setSelectedTags] = useState([])

  const [confirmed, setConfirmed] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)

  const handleAction = action => {
    if (confirmed) {
      if (action === "generate") {
        const pw = generatePassword(14)
        validation.setFieldValue("password", pw)
      }
      return
    }
    setPendingAction(action)
  }

  const handleProceed = () => {
    setConfirmed(true)
    if (pendingAction === "generate") {
      const pw = generatePassword(14)
      validation.setFieldValue("password", pw)
    }
    setPendingAction(null)
  }

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      setUserRole(role)
    }
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    setSearchText("")
    if (props.selectedRow) {
      getData()
      setClients([])
    }
  }, [props.selectedRow])

  useEffect(() => {
    if (activeTab == "2") {
      getLinkableClients()
    }
  }, [linksGroup, userData, activeTab])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, clients])

  // Get admins data
  const getData = () => {
    if (props.selectedRow?.key) {
      setLoading(true)
      setUserData(null)
      getApplicationUser(abortRef.current, props.selectedRow.key)
        .then(result => {
          if (result.statusCode == 200 && result.data) {
            setUserData(result.data)
            //setLinksGroup([result.data.group])
            //setLinksGroup([])
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Get linkable clients for link management
  const getLinkableClients = () => {
    if (userData?.key && !update) {
      // console.log("fetch getAppUsersLinks")
      setUpdate(true)
      setClients([])
      getAppUsersLinks(abortRef.current, userData.key, linksGroup)
        .then(result => {
          if (result.statusCode == 200 && result.entries) {
            // console.log("fetch getAppUsersLinks results ", result)
            result.entries.sort((a, b) =>
              a.clientName > b.clientName ? 1 : -1
            )
            setClients(result.entries)
          } else {
            showToast(props.t("Error"), "Error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "Error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Filter unchanged values
  const filterUnchangedValues = (originalValues, newValues) => {
    const changedValues = {}

    for (const key in newValues) {
      if (!isEqual(originalValues[key], newValues[key])) {
        changedValues[key] = newValues[key]
      }
    }
    return changedValues
  }

  // Update client validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userData?.name || "",
      group: userData?.group || "",
      gsm: userData?.gsm || "",
      email: userData?.email || "",
      language: userData?.language || "",
      unit: userData?.unit || "",
      password: "",
      relative: userData?.relative || false,
      securityPerson: userData?.securityPerson || false,
      notificationsDisabled: userData?.notificationsDisabled || false,
      messageNotifications: userData?.messageNotifications || false,
      alarmsDisabled: userData?.alarmsDisabled || false,
      personalJournalOnly: userData?.personalJournalOnly || false,
      videoRoom: userData?.videoRoom || null,
    },
    validationSchema: Yup.object({
      password: Yup.string().test(
        "pw",
        props.t("Password is too short"),
        val => !val || val.length >= 14
      ),
    }),
    onSubmit: values => {
      console.log("Submit values: ", values)
      // Filter out unchanged values
      const initialFormValues = structuredClone(validation.initialValues)
      const changedValues = filterUnchangedValues(initialFormValues, values)
      //console.log("Filtered changed: ", changedValues)
      let temp = { ...changedValues }
      //console.log("Filtered submit values: ", temp)

      setSuccess("")
      setError("")
      if (userData?.key && !update) {
        setUpdate(true)
        updateApplicationUser(abortRef.current, userData?.key, temp)
          .then(result => {
            if (result.statusCode == 200) {
              setSuccess("Updated")
              getData()
              props.getUsers()
            } else {
              setError("Error")
            }
          })
          .catch(err => {
            console.log(err)
            setError("Error")
          })
          .finally(() => {
            setUpdate(false)
          })
      }
    },
  })

  // Send credentials
  const sendCredentials = () => {
    if (userData?.key && !update) {
      //console.log("Send creds: ", userData.key, sms)
      setUpdate(true)
      sendAppUserCredentials(abortRef.current, userData.key, sms)
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Sent"), "success")
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Link selected clients to user
  const linkClients = () => {
    if (userData?.key && !update && selectedClients.length > 0) {
      setUpdate(true)
      linkClientsToAppUser(abortRef.current, userData.key, selectedClients)
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            setSelectedClients([])
            getLinkableClients()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Unlink selected clients from user
  const unlinkClients = () => {
    if (userData?.key && !update && selectedClients.length > 0) {
      setUpdate(true)
      unlinkClientsFromAppUser(abortRef.current, userData.key, selectedClients)
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            setSelectedClients([])
            getLinkableClients()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Group selection changed in users info
  const groupChanged = newGroup => {
    validation.setFieldValue("group", newGroup)
  }

  // Group selection changed in links management
  const groupChanged2 = newGroup => {
    setLinksGroup(newGroup)
  }

  const handleSearch = () => {
    const delayDebounce = setTimeout(() => {
      const lower = searchText.toLowerCase()
      const filtered = clients.filter(item => {
        const clientName = item.clientName?.toLowerCase() || ""

        return clientName.includes(lower)
      })
      setFilteredClients(filtered)
    }, 300)

    return () => clearTimeout(delayDebounce)
  }

  // Table structure
  const columns = [
    {
      dataIndex: "linked",
      title: props.t("Linked"),
      defaultSortOrder: "descend",
      align: "center",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "linked", sortOrder)
      },
      render: checkboxFormatter,
      width: "75px",
      ellipsis: true,
    },
    {
      dataIndex: "clientName",
      title: props.t("Client"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "clientName", sortOrder)
      },
      width: "500px",
      ellipsis: true,
    },
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "group", sortOrder)
      },
      width: "100px",
      ellipsis: true,
    },
  ]

  const selectRow = {
    selectedRowKeys: selectedClients,
    onSelect: (row, isSelect, rowIndex, e) => {
      if (isSelect) {
        setSelectedClients(prevState => [...prevState, row.clientKey])
      } else {
        setSelectedClients(prevState =>
          prevState.filter(prevItem => prevItem !== row.clientKey)
        )
      }
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        rows.map(row =>
          setSelectedClients(prevState => [...prevState, row.clientKey])
        )
      } else {
        setSelectedClients([])
      }
    },
  }
  return (
    <>
      <Drawer
        title={props.selectedRow?.username}
        width={"80%"}
        placement="right"
        onClose={() => {
          props.setShowDrawer(false)
          setActiveTab("1")
          setClients([])
          setLinksGroup([])
          setSelectedClients([])
          setError("")
          setSuccess("")
          setSms(
            "Lataa Suvanto Care -sovellus osoitteesta Suvantocare.fi/lataa tai puhelimesi sovelluskaupasta. Löydät lisää ohjeita osoitteesta https://suvanto.help. \n\nMobiilikäyttäjätunnus on muotoa etu.sukunimi ja salasana"
          )
          setConfirmed(false)
        }}
        open={props.showDrawer}
        zIndex={1003}
      >
        {loading && (
          <Lottie
            options={getLottieLoadingOptions()}
            height={100}
            width={100}
          ></Lottie>
        )}

        {!loading && userData && (
          <>
            <div className="d-flex flex-wrap mb-3">
              <ul className="nav icon-tab">
                <NavItem>
                  <NavLink
                    to="#"
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => {
                      setActiveTab("1")
                    }}
                  >
                    {props.t("Information and settings")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => {
                      setActiveTab("2")
                    }}
                  >
                    {props.t("Manage users links")}
                  </NavLink>
                </NavItem>
              </ul>
            </div>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <Row xs={1} xl={2}>
                  <Col xs={12} xl={7}>
                    <Card>
                      <CardBody>
                        <CardTitle className="mb-4">
                          {props.t("User information")}
                        </CardTitle>
                        {loading && (
                          <Lottie
                            options={getLottieLoadingOptions()}
                            height={100}
                            width={100}
                          ></Lottie>
                        )}
                        {!loading && (
                          <Form
                            autoComplete="nope"
                            className="form-horizontal"
                            onSubmit={e => {
                              e.preventDefault()
                              return false
                            }}
                          >
                            {error ? (
                              <Alert color="danger">{props.t(error)}</Alert>
                            ) : null}
                            {success ? (
                              <Alert color="success">{props.t(success)}</Alert>
                            ) : null}
                            <Row xs={1} md={2}>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("Name")}</Label>
                                  <AntdInput
                                    allowClear
                                    placeholder={props.t("Name")}
                                    value={validation.values.name}
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "name",
                                        v.target.value
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("Group")}</Label>
                                  <SelectGroupFilters
                                    onChange={groupChanged}
                                    multiple={false}
                                    selection={validation.values.group}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("GSM")}</Label>
                                  <AntdInput
                                    allowClear
                                    placeholder={props.t("GSM")}
                                    value={validation.values.gsm}
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "gsm",
                                        v.target.value
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("Email")}</Label>
                                  <AntdInput
                                    allowClear
                                    placeholder={props.t("Email")}
                                    value={validation.values.email}
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "email",
                                        v.target.value
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("Language")}</Label>
                                  <Select
                                    value={validation.values.language}
                                    placeholder={props.t("Choose")}
                                    onChange={v => {
                                      validation.setFieldValue("language", v)
                                    }}
                                    options={[
                                      {
                                        label: props.t("English"),
                                        value: "en",
                                      },
                                      {
                                        label: props.t("Finnish"),
                                        value: "fi-FI",
                                      },
                                      {
                                        label: props.t("Swedish"),
                                        value: "sv-SE",
                                      },
                                    ]}
                                    style={{ width: 100 + "%" }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("Unit info")}</Label>
                                  <AntdInput
                                    allowClear
                                    placeholder={props.t("Unit info")}
                                    value={validation.values.unit}
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "unit",
                                        v.target.value
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              {userRole == "system_admin" && (
                                <Col>
                                  <FormGroup>
                                    <Label>
                                      {props.t("Video number") +
                                        " (" +
                                        props.t("Callback") +
                                        ")"}
                                    </Label>
                                    <AntdInput
                                      allowClear
                                      placeholder={props.t("Video number")}
                                      value={validation.values.videoRoom}
                                      onChange={v => {
                                        validation.setFieldValue(
                                          "videoRoom",
                                          v.target.value
                                        )
                                      }}
                                    />
                                  </FormGroup>
                                </Col>
                              )}
                              <Col>
                                <FormGroup>
                                  <Label>{props.t("New password")}</Label>
                                  <AntdForm.Item
                                    validateStatus={
                                      validation.errors.password ? "error" : ""
                                    }
                                    hasFeedback
                                    help={validation.errors.password}
                                  >
                                    <Search
                                      allowClear
                                      enterButton={
                                        <Tooltip
                                          title={props.t(
                                            "Generate new password"
                                          )}
                                          key="genPwButton"
                                          placement="bottom"
                                        >
                                          <i className="mdi mdi-shield-refresh font-size-16" />
                                        </Tooltip>
                                      }
                                      onClick={() => handleAction("focus")}
                                      onSearch={() => {
                                        handleAction("generate")
                                      }}
                                      placeholder={props.t("Password")}
                                      value={validation.values.password}
                                      onChange={v => {
                                        validation.setFieldValue(
                                          "password",
                                          v.target.value
                                        )
                                      }}
                                    />
                                  </AntdForm.Item>
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row xs={1} md={2}>
                              <Col>
                                <FormGroup className="mt-2">
                                  <Label>{props.t("Relative")}</Label>
                                  <Switch
                                    className="d-block"
                                    name="relative"
                                    checked={validation.values.relative}
                                    onChange={v => {
                                      validation.setFieldValue("relative", v)
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              {userRole == "system_admin" && (
                                <Col>
                                  <FormGroup className="mt-2">
                                    <Label>{props.t("Security person")}</Label>
                                    <Switch
                                      className="d-block"
                                      name="relative"
                                      checked={validation.values.securityPerson}
                                      onChange={v => {
                                        validation.setFieldValue(
                                          "securityPerson",
                                          v
                                        )
                                      }}
                                    />
                                  </FormGroup>
                                </Col>
                              )}
                              <Col>
                                <FormGroup className="mt-2">
                                  <Label>
                                    {props.t("Disable alarm notifications")}
                                  </Label>
                                  <Switch
                                    className="d-block"
                                    name="notificationsDisabled"
                                    checked={
                                      validation.values.notificationsDisabled
                                    }
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "notificationsDisabled",
                                        v
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup className="mt-2">
                                  <Label>
                                    {props.t("Notify new messages")}
                                  </Label>
                                  <Switch
                                    className="d-block"
                                    name="messageNotifications"
                                    checked={
                                      validation.values.messageNotifications
                                    }
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "messageNotifications",
                                        v
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup className="mt-2">
                                  <Label>
                                    {props.t("Disable and hide alarms")}
                                  </Label>
                                  <Switch
                                    className="d-block"
                                    name="alarmsDisabled"
                                    checked={validation.values.alarmsDisabled}
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "alarmsDisabled",
                                        v
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup className="mt-2">
                                  <Label>
                                    {props.t(
                                      "Show only personal journal entries"
                                    )}
                                  </Label>
                                  <Switch
                                    className="d-block"
                                    name="personalJournalOnly"
                                    checked={
                                      validation.values.personalJournalOnly
                                    }
                                    onChange={v => {
                                      validation.setFieldValue(
                                        "personalJournalOnly",
                                        v
                                      )
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <div className="mt-2 mb-3 d-flex justify-content-center">
                              <Button
                                color="success"
                                disabled={update}
                                style={{ minWidth: "150px" }}
                                onClick={() => {
                                  validation.submitForm()
                                }}
                              >
                                {props.t("Save")}
                              </Button>
                            </div>
                          </Form>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xs={12} xl={5}>
                    <Card>
                      <CardBody>
                        <CardTitle className="mb-4">
                          {props.t("Send login credentials")}
                        </CardTitle>
                        {userRole == "system_admin" ? (
                          <FormGroup>
                            <Label>{props.t("SMS")}</Label>
                            <TextArea
                              allowClear
                              rows={4}
                              value={sms}
                              onChange={v => {
                                setSms(v.target.value)
                              }}
                            />
                          </FormGroup>
                        ) : (
                          <Alert color="info">
                            <span>
                              {props.t("sendLoginCredentialsInfo")}
                              <br />
                            </span>
                            <br />
                            <span>
                              {props.t(
                                "System generates password automatically"
                              )}
                            </span>
                          </Alert>
                        )}

                        <div className="mt-2 mb-3 d-flex justify-content-center">
                          <Button
                            color="primary"
                            disabled={!userData?.gsm || update}
                            style={{ minWidth: "150px" }}
                            onClick={() => {
                              sendCredentials()
                            }}
                          >
                            {props.t("Send")}
                          </Button>
                        </div>

                        {userRole == "system_admin" && (
                          <div className="mb-3 d-flex justify-content-center">
                            <Button
                              color="secondary"
                              style={{ minWidth: "150px" }}
                              disabled={!userData?.gsm}
                              onClick={() => {
                                globalThis.open(
                                  "/logs/sms?recipient=" + userData?.gsm,
                                  "_blank",
                                  "noreferrer"
                                )
                              }}
                            >
                              {props.t("View SMS history")}
                            </Button>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="2">
                <Card>
                  <CardBody>
                    <React.Fragment>
                      <Row>
                        <Col className="col-auto mb-3">
                          <Search
                            placeholder={props.t("Search")}
                            allowClear
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: "200px" }}
                          />
                        </Col>
                        <Col
                          className="col-auto mb-3"
                          style={{ minWidth: "300px", maxWidth: "400px" }}
                        >
                          <TagsTree
                            loadData={true}
                            expanded={true}
                            selectionChanged={setSelectedTags}
                          />
                        </Col>
                        <Col
                          className="mb-3"
                          style={{ minWidth: "200px", maxWidth: "400px" }}
                        >
                          <SelectGroupFilters
                            onChange={groupChanged2}
                            multiple={true}
                            clearable={true}
                            selection={linksGroup}
                            selectedTags={selectedTags}
                          />
                        </Col>
                        <Col>
                          <div className="mt-2 float-end">
                            {filteredClients.length == 1 ? (
                              <>
                                {filteredClients.length +
                                  " " +
                                  props.t("found result")}
                              </>
                            ) : (
                              <>
                                {filteredClients.length +
                                  " " +
                                  props.t("found results")}
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Collapse isOpen={selectedClients.length > 0}>
                        <Row className="mb-3">
                          <Col className="col-auto">
                            <Button
                              color="success"
                              className="compact-button"
                              disabled={selectedClients.length == 0 || update}
                              onClick={() => {
                                linkClients()
                              }}
                            >
                              <i className="mdi mdi-link-variant-plus me-2" />
                              {props.t("Link selected clients")}
                            </Button>
                            <Button
                              color="danger"
                              className="ms-3 compact-button"
                              disabled={selectedClients.length == 0 || update}
                              onClick={() => {
                                unlinkClients()
                              }}
                            >
                              <i className="mdi mdi-link-variant-off me-2" />
                              {props.t("Unlink selected clients")}
                            </Button>
                          </Col>
                          <Col className="mt-2">
                            {selectedClients.length == 1 ? (
                              <>
                                {selectedClients.length +
                                  " " +
                                  props.t("selected user")}
                              </>
                            ) : (
                              <>
                                {selectedClients.length +
                                  " " +
                                  props.t("selected users")}
                              </>
                            )}
                          </Col>
                        </Row>
                      </Collapse>

                      <Row className="mt-1">
                        <Col>
                          <AntdTable
                            className="narrow-header"
                            bordered={true}
                            size="small"
                            loading={loading}
                            rowKey={"clientKey"}
                            rowSelection={selectRow}
                            columns={columns}
                            dataSource={filteredClients}
                            tableLayout="auto"
                            pagination={{
                              showSizeChanger: true,
                              defaultPageSize: 50,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </Col>
                      </Row>
                    </React.Fragment>
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </>
        )}
      </Drawer>

      {/** New password explainer modal */}
      <Modal
        isOpen={pendingAction !== null}
        toggle={() => {
          setPendingAction(null)
        }}
        centered
      >
        <ModalBody>
          <Alert color="info">
            <p>{props.t("sendLoginPreferred")}</p>
            <p>{props.t("loginMessageGeneratesPassword")}</p>
            <p>{props.t("manualPasswordDisablesAutoSend")}</p>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setPendingAction(null)
            }}
          >
            {props.t("Cancel")}
          </Button>
          <Button
            color="primary"
            onClick={() => {
              handleProceed()
            }}
          >
            {props.t("OK")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

AppUserDrawer.propTypes = {
  t: PropTypes.any,
  showDrawer: PropTypes.bool,
  setShowDrawer: PropTypes.any,
  selectedRow: PropTypes.any,
  getUsers: PropTypes.func,
}

export default withRouter(withTranslation()(AppUserDrawer))
